/**
 * collectPerfProbe — client performance diagnostic covariates for UFO payloads.
 *
 * Purpose: existing metrics (TBT, page-load timings) tell us the main thread was
 * blocked or the load was slow, but not *why*. This module emits a small set of
 * explanatory covariates so the "slow client" bucket can be decomposed into:
 *   (a) slow per-core CPU                → `perfProbe:cpuMs`
 *   (b) GPU / software-rendering fallback → `perfProbe:softwareGL`
 *   (c) HTML buffered upstream (e.g. AV)  → `perfProbe:scriptStartAfterHtml`
 *
 * Signals split into two kinds:
 *   - The CPU benchmark (cpuMs): the only expensive signal, so it is measured off
 *     the critical path in idle time and persisted via the Confluence storage
 *     manager (local only) along with the epoch ms it was taken (`perfProbe:t`), so
 *     the initial-load payload can carry the previous load's sample without ever
 *     blocking the current load.
 *   - Cheap per-load reads (softwareGL, scriptStartAfterHtml): computed inline when
 *     the payload is generated — never persisted — since a canvas/WebGL probe and a
 *     Performance Timeline read are both trivial.
 *
 * Performance-safety contract: this module must never affect the page it measures.
 * The CPU benchmark only ever runs inside requestIdleCallback — with no timer
 * fallback, so it is simply skipped where that API is unavailable — one small sample
 * per idle slot; every entry point is wrapped so any failure yields an empty object.
 */

import { getLogger } from '@confluence/logger';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';

const logger = getLogger('collectPerfProbe');

const SCHEMA_VERSION = 1;

// Number of timed runs of the fixed workload; we report the min (contention
// stripped out). Runs one-per-idle-slot so we always yield between samples.
const SAMPLE_COUNT = 5;
// Fixed iteration count. Chosen so a single sample takes a few ms — comfortably
// above performance.now() resolution (which browsers clamp for privacy) yet small
// enough to fit inside a single idle slot. Never adaptive.
const ITERATIONS = 2_000_000;

const IDLE_TIMEOUT_MS = 60_000;

// Renderer substrings that indicate hardware acceleration is off and WebGL is
// running on a software rasteriser.
const SOFTWARE_RENDERER_HINTS = ['swiftshader', 'llvmpipe', 'basic render driver', 'software'];

interface PersistedProbe {
	v: number;
	cpuMs: number;
	// Epoch ms of when this sample was generated, so a consumer can tell how stale
	// the CPU measurement is.
	t: number;
}

type ProbePayload = Record<string, number | boolean | string>;

const round2 = (n: number): number => Math.round(n * 100) / 100;

let refreshRunning = false;
let pendingSamples: number[] = [];

/**
 * Read the perfProbe covariates for the current initial-load payload.
 *
 * Returns the previous load's persisted CPU sample (with the epoch ms it was taken;
 * both empty on a device's very first ever load) plus the current load's inline
 * signals (softwareGL, HTML-buffering), and schedules a fresh CPU measurement in
 * idle time for the next load. The only per-call work is a cheap canvas/WebGL probe
 * and a Performance Timeline read — never the CPU benchmark.
 */
export function readPerfProbe(): ProbePayload {
	const payload: ProbePayload = {};

	try {
		const stored = readStored();
		if (stored) {
			payload['perfProbe:cpuMs'] = stored.cpuMs;
			payload['perfProbe:t'] = stored.t;
			// Consume-on-send: clear the persisted sample once it's been read into a
			// payload so a stale CPU measurement is never reported twice. The refresh
			// scheduled below writes a fresh sample for the next load.
			clearStored();
		}
		// Refresh the CPU sample off the critical path for next time.
		scheduleRefresh();
	} catch {
		// never let the CPU covariate affect the payload
	}

	try {
		// Cheap to compute, so read it live rather than persisting it.
		if (typeof document !== 'undefined') {
			payload['perfProbe:softwareGL'] = detectSoftwareGL();
		}
	} catch {
		// GL probe is best-effort
	}

	try {
		payload['perfProbe:scriptStartAfterHtml'] = getScriptStartAfterHtml();
	} catch {
		// per-load timing is best-effort
	}

	return payload;
}

// --- (a) CPU benchmark: idle sampling + persistence ---------------------------

function scheduleRefresh(): void {
	if (refreshRunning) {
		return;
	}
	refreshRunning = true;
	pendingSamples = [];
	scheduleNextSample();
}

function scheduleNextSample(): void {
	requestIdle(() => {
		if (pendingSamples.length === 0) {
			logger.info`collectPerfProbe: running CPU benchmark — ${SAMPLE_COUNT} samples of ${ITERATIONS} iterations`;
		}
		let sample: number;
		try {
			sample = runCpuSample();
		} catch {
			// abandon this cycle; leave any previously stored sample untouched
			refreshRunning = false;
			return;
		}
		pendingSamples.push(sample);

		if (pendingSamples.length < SAMPLE_COUNT) {
			scheduleNextSample();
			return;
		}

		try {
			writeStored({
				v: SCHEMA_VERSION,
				cpuMs: round2(Math.min(...pendingSamples)),
				t: Date.now(),
			});
		} catch {
			// persistence failure is non-fatal
		} finally {
			refreshRunning = false;
		}
	});
}

// Sink on the global object. Writing the checksum somewhere externally observable
// stops dead-code elimination from proving the loop's result is unused and
// deleting the benchmark (see design doc, "DCE guard"). One property write.
interface ProbeGlobal {
	__ccPerfProbeChecksum?: number;
}

function runCpuSample(): number {
	let checksum = 0;
	const start = performance.now();
	for (let i = 0; i < ITERATIONS; i++) {
		// Unsigned 32-bit wrap keeps the checksum a small int and is intrinsic to the
		// fixed workload we're timing; the bitwise op is deliberate here.
		// eslint-disable-next-line no-bitwise
		checksum = (checksum * 31 + i) >>> 0;
	}
	const elapsed = performance.now() - start;
	(globalThis as unknown as ProbeGlobal).__ccPerfProbeChecksum = checksum;
	return elapsed;
}

// --- (b) software-rendering probe: cheap inline canvas/WebGL read -------------

function detectSoftwareGL(): boolean {
	// Guard browser globals for SSR; callers already gate on `document`, but the
	// access is kept lexically inside the positive guard.
	if (typeof document !== 'undefined') {
		const canvas = document.createElement('canvas');
		canvas.width = 1;
		canvas.height = 1;
		const gl = (canvas.getContext('webgl') ||
			canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
		if (!gl) {
			// No hardware-accelerated WebGL path available at all.
			return true;
		}

		let renderer = '';
		const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
		if (debugInfo) {
			renderer = String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '');
		}
		// Release the context promptly rather than waiting for GC.
		gl.getExtension('WEBGL_lose_context')?.loseContext();

		const normalized = renderer.toLowerCase();
		// If the renderer string is unavailable (some browsers restrict it) we
		// cannot tell — default to false so we never produce a false (b) positive.
		return SOFTWARE_RENDERER_HINTS.some((hint) => normalized.includes(hint));
	}

	return false;
}

// --- (c) per-load HTML-buffering signal: passive timeline read ----------------

/**
 * Did JS only start downloading after the HTML document had fully arrived?
 *
 *   scriptStartAfterHtml = firstScript.startTime >= navigation.responseEnd
 *
 * false → the preload scanner discovered a <script> while the HTML was still
 *         streaming and started fetching it early (healthy streaming).
 * true  → no JS fetch began until the whole HTML had arrived, i.e. the document was
 *         buffered upstream (AV/content-inspection proxy is the leading suspect on
 *         managed fleets).
 * 'unknown' → the timeline could not be read (no Performance API, no navigation
 *         entry, or no non-cached script resource to anchor on).
 *
 * Anchored on the earliest-started script — the best case. If even that one only
 * started after the full HTML, buffering is strongly indicated. Cache hits are
 * excluded (their timing does not reflect network delivery).
 */
function getScriptStartAfterHtml(): boolean | 'unknown' {
	if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
		return 'unknown';
	}

	const nav = performance.getEntriesByType('navigation')[0] as
		| PerformanceNavigationTiming
		| undefined;
	if (!nav || !nav.responseEnd) {
		return 'unknown';
	}

	const scripts = (performance.getEntriesByType('resource') as PerformanceResourceTiming[]).filter(
		(entry) => entry.initiatorType === 'script' && entry.startTime > 0 && entry.transferSize > 0,
	);
	if (scripts.length === 0) {
		return 'unknown';
	}

	const earliest = scripts.reduce((a, b) => (a.startTime <= b.startTime ? a : b));
	return earliest.startTime >= nav.responseEnd;
}

// --- persistence via the sanctioned Confluence storage manager ----------------
// Local-only key (not in PERSISTED_KEYS_ON_SERVER), so nothing syncs to the
// server. The instance is a module singleton initialised at app boot; it may be
// undefined very early, and its own methods no-op during SSR.

function readStored(): PersistedProbe | undefined {
	try {
		const raw = confluenceLocalStorageInstance?.getItem(keys.PERF_PROBE_DIAGNOSTIC);
		if (!raw) {
			return undefined;
		}
		const parsed = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Partial<PersistedProbe>;
		if (
			parsed &&
			parsed.v === SCHEMA_VERSION &&
			typeof parsed.cpuMs === 'number' &&
			typeof parsed.t === 'number'
		) {
			return { v: parsed.v, cpuMs: parsed.cpuMs, t: parsed.t };
		}
		return undefined;
	} catch {
		return undefined;
	}
}

function writeStored(value: PersistedProbe): void {
	try {
		confluenceLocalStorageInstance?.setItem(keys.PERF_PROBE_DIAGNOSTIC, JSON.stringify(value));
	} catch {
		// quota / disabled storage — non-fatal
	}
}

function clearStored(): void {
	try {
		confluenceLocalStorageInstance?.removeItem(keys.PERF_PROBE_DIAGNOSTIC);
	} catch {
		// disabled storage — non-fatal
	}
}

function requestIdle(callback: () => void): void {
	// In Confluence bundles `window` is replaced at build time and always exists, so a
	// `typeof window` check is not a valid SSR guard — probe the specific API instead
	// (Confluence SSR rule §2). Only run the benchmark when requestIdleCallback is
	// available: we never want to schedule the compute workload on a timer that could
	// fire while the main thread is busy. No fallback — if it's unavailable, the sample
	// is simply skipped.
	if (typeof window.requestIdleCallback === 'function') {
		window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
	}
}
